import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { matchPath, useHistory, useLocation } from 'react-router-dom';
import AppstoreAddOutlined from '@ant-design/icons/AppstoreAddOutlined';
import CheckCircleFilled from '@ant-design/icons/CheckCircleFilled';
import CodeFilled from '@ant-design/icons/CodeFilled';
import CreditCardFilled from '@ant-design/icons/CreditCardFilled';
import CustomerServiceFilled from '@ant-design/icons/CustomerServiceFilled';
import FileTextFilled from '@ant-design/icons/FileTextFilled';
import HomeFilled from '@ant-design/icons/HomeFilled';
import MenuOutlined from '@ant-design/icons/MenuOutlined';
import ProfileFilled from '@ant-design/icons/ProfileFilled';
import QrcodeOutlined from '@ant-design/icons/QrcodeOutlined';
import ReadFilled from '@ant-design/icons/ReadFilled';
import SettingFilled from '@ant-design/icons/SettingFilled';
import ShopFilled from '@ant-design/icons/ShopFilled';
import SignalFilled from '@ant-design/icons/SignalFilled';
import CalendarCheckFilled from '@distribusion/portal-icons/dist/CalendarCheckFilled';
import CalendarCrossFilled from '@distribusion/portal-icons/dist/CalendarCrossFilled';
import CalendarFilled from '@distribusion/portal-icons/dist/CalendarFilled';
import CalendarReturnFilled from '@distribusion/portal-icons/dist/CalendarReturnFilled';
import CoinsFilled from '@distribusion/portal-icons/dist/CoinsFilled';
import CustomizationFilled from '@distribusion/portal-icons/dist/CustomizationFilled';
import PaypalFilled from '@distribusion/portal-icons/dist/PaypalFilled';
import TradingDeskFilled from '@distribusion/portal-icons/dist/TradingDeskFilled';
import UserCheckedFilled from '@distribusion/portal-icons/dist/UserCheckedFilled';
import UsersFilled from '@distribusion/portal-icons/dist/UsersFilled';
import { FullScreenSpin } from '@distribusion/portal-lib/components';
import { DEFAULT_TAB_TITLE } from '@distribusion/portal-lib/constants';
import {
  PerformanceMarksEnum,
  useFramePaint,
  usePageActivationAsNavigationMark,
} from '@distribusion/portal-lib/metrics';
import { useBreakpoint } from '@distribusion/portal-lib/responsive';
import { setDocumentTitle } from '@distribusion/portal-lib/utils';
import { insertIf } from '@distribusion/portal-lib/utils';
import { Button, Layout as LayoutBase, MenuProps, message, notification } from 'antd';
import {
  MenuDividerType,
  MenuItemGroupType,
  MenuItemType,
  SubMenuType,
} from 'antd/es/menu/hooks/useItems';
import { usePermissions } from 'api/permissions';
import { ROUTES } from 'constants/common';
import { useFirstPartnerPreferencesIfExist } from 'features/settings/api/preferences';
import { amplitude, AmplitudeEventEnum } from 'lib/analytics';
import { featureFlags } from 'lib/feature-flags';
import { useTranslation } from 'lib/translation';
import { nanoid } from 'nanoid';

import { Header, SideNav } from './components';

type MenuItemTypeWithTestId = Omit<MenuItemType, 'children'> & { 'data-testid'?: string };
type MenuItemGroupTypeWithTestId = Omit<MenuItemGroupType, 'children'> & {
  children?: ItemTypeWithTestId[];
};
type ItemTypeWithTestId =
  | MenuItemTypeWithTestId
  | SubMenuType
  | MenuItemGroupTypeWithTestId
  | MenuDividerType
  | null;

const ROUTE_PARAMS = {
  [ROUTES.HOME]: { exact: true },
  [ROUTES.BOOKING_MANAGEMENT.NEW]: { exact: true },
  [ROUTES.BOOKING_MANAGEMENT.TICKET_VALIDATOR]: { exact: true },
};

const getSelectedItems = (
  items: ItemTypeWithTestId[],
  location: { pathname: string },
): MenuItemTypeWithTestId[] => {
  const selectedItems = items.reduce((acc, item) => {
    if ('children' in item) {
      return acc.concat(getSelectedItems(item.children, location));
    }
    if (
      matchPath(location.pathname, {
        path: item.key as string,
        ...ROUTE_PARAMS[item.key as string],
      })
    ) {
      return acc.concat(item);
    }
    return acc;
  }, []);

  if (selectedItems.length > 1) {
    const exactPaths = selectedItems.filter((item) => ROUTE_PARAMS[item.key]?.exact);
    const bestMatch = selectedItems.reduce((a: string, b: string) => (a.length > b.length ? a : b));

    return exactPaths.length ? exactPaths : [bestMatch];
  }

  return selectedItems;
};

interface LayoutProps {
  children?: ReactNode;
}

export const Layout = ({ children }: LayoutProps) => {
  const [mobileNavOpened, setMobileNavOpened] = useState(false);
  const { data: permissions, isLoading, isFetched, isError } = usePermissions();
  const { data: preferences } = useFirstPartnerPreferencesIfExist();

  const { t } = useTranslation();
  const { lg } = useBreakpoint();
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    if (isError) {
      message.error(t('layout.error_messages.failed_to_fetch_permissions'));
    }
  }, [isError, t]);

  const handleSelect: MenuProps['onSelect'] = ({ key }) => {
    if (key === ROUTES.API_DOCS) {
      amplitude.track(AmplitudeEventEnum.VIEW_DOCS);
      window.open(key, '_blank');
      return;
    }
    if (key === ROUTES.SERVICE_DESK) {
      amplitude.track(AmplitudeEventEnum.VIEW_SERVICE_DESK);
      window.open(key, '_blank');
      return;
    }
    history.push(key);
  };

  const navMenuItems = useMemo(
    () =>
      [
        {
          key: ROUTES.DASHBOARDS.WELCOME,
          icon: <HomeFilled />,
          label: t('nav.home'),
          'data-testid': 'home-nav-link',
        },
        ...insertIf(
          permissions?.view_ticket_validator || permissions?.view_passenger_manifest,
          {
            type: 'divider',
          },
          {
            type: 'group',
            label: t('nav.group_driver_operations').toUpperCase(),
            children: [
              ...insertIf(permissions?.view_ticket_validator, {
                key: ROUTES.BOOKING_MANAGEMENT.TICKET_VALIDATOR,
                icon: <QrcodeOutlined />,
                label: t('nav.ticket_validator'),
                title: t('nav_titles.ticket_validator'),
                'data-testid': 'ticket-validator-nav-link',
              }),
              ...insertIf(permissions?.view_passenger_manifest, {
                key: ROUTES.DASHBOARDS.PASSENGER_MANIFEST,
                icon: <UserCheckedFilled />,
                label: t('nav.passenger_manifest'),
                'data-testid': 'passenger-manifest-nav-link',
              }),
            ],
          },
        ),
        ...insertIf(
          permissions?.view_sales_dashboard ||
            permissions?.view_sla ||
            permissions?.view_ancillary_insights ||
            permissions?.view_departure_deep_dive,
          {
            type: 'divider',
          },
          {
            type: 'group',
            label: t('nav.group_data_insights').toUpperCase(),
            children: [
              ...insertIf(
                permissions?.view_sales_dashboard ||
                  permissions?.view_ancillary_insights ||
                  permissions?.view_departure_deep_dive,
                {
                  key: ROUTES.DASHBOARDS.PARTNER,
                  icon: <SignalFilled />,
                  label: t('nav.sales_dashboards'),
                  'data-testid': 'sales-dashboard-nav-link',
                },
              ),
              ...insertIf(permissions?.view_sla, {
                key: ROUTES.DASHBOARDS.SLA,
                icon: <CheckCircleFilled />,
                label: t('nav.SLA'),
                'data-testid': 'sla-nav-link',
              }),
            ],
          },
        ),
        ...insertIf(
          permissions?.view_bookings ||
            permissions?.view_cancellations_page ||
            permissions?.manage_subscriptions,
          {
            type: 'divider',
          },
          {
            type: 'group',
            label: t('nav.group_daily_operations').toUpperCase(),
            children: [
              ...insertIf(permissions?.view_bookings, {
                key: ROUTES.BOOKING_MANAGEMENT.INDEX,
                icon: <CalendarFilled />,
                label: t('nav.bookings'),
                'data-testid': 'bookings-nav-link',
              }),
              ...insertIf(permissions?.view_cancellations_page, {
                key: ROUTES.BOOKING_MANAGEMENT.CANCELLATIONS,
                icon: <CalendarCrossFilled />,
                label: t('nav.cancellations'),
                'data-testid': 'cancellations-nav-link',
              }),
              ...insertIf(permissions?.manage_subscriptions, {
                key: ROUTES.BOOKING_MANAGEMENT.SUBSCRIPTIONS,
                icon: <CalendarReturnFilled />,
                label: t('nav.subscriptions'),
                'data-testid': 'subscriptions-nav-link',
              }),
            ],
          },
        ),
        ...insertIf(
          permissions?.view_global_inventory ||
            permissions?.view_trading_desk ||
            permissions?.view_retailer_tab,
          {
            type: 'divider',
          },
          {
            type: 'group',
            label: t('nav.group_network').toUpperCase(),
            children: [
              ...insertIf(permissions?.view_global_inventory, {
                key: ROUTES.DASHBOARDS.GLOBAL_INVENTORY,
                icon: <ProfileFilled />,
                label: t('nav.global_inventory_dashboard'),
                'data-testid': 'global-inventory-dashboard-nav-link',
              }),
              ...insertIf(permissions?.view_trading_desk, {
                key: ROUTES.TRADING_DESK.INDEX,
                icon: <TradingDeskFilled />,
                label: t('nav.trading_desk'),
                'data-testid': 'trading-desk-nav-link',
              }),
              ...insertIf(permissions?.view_retailer_tab, {
                key: ROUTES.TRADING_DESK.RETAILERS,
                icon: <ShopFilled />,
                label: t('nav.retailers'),
                'data-testid': 'retailers-nav-link',
              }),
            ],
          },
        ),
        ...insertIf(
          permissions?.view_book_a_trip ||
            permissions?.view_guidelines ||
            permissions?.view_payment_information ||
            permissions?.view_paypal_commission,
          {
            type: 'divider',
          },
          {
            type: 'group',
            label: t('nav.group_agency_counter').toUpperCase(),
            children: [
              ...insertIf(permissions?.view_book_a_trip, {
                key: ROUTES.BOOKING_MANAGEMENT.NEW,
                icon: <CalendarCheckFilled />,
                label: t('nav.new_booking'),
                'data-testid': 'create-booking-nav-link',
              }),
              ...insertIf(permissions?.view_payment_information, {
                key: ROUTES.AGENCY_MANAGEMENT.PAYMENT_INFORMATION,
                icon: <CreditCardFilled />,
                label: t('nav.payment_information'),
                'data-testid': 'payment-info-nav-link',
              }),
              ...insertIf(permissions?.view_guidelines, {
                key: ROUTES.AGENCY_MANAGEMENT.GUIDELINES,
                icon: <ReadFilled />,
                label: t('nav.guidelines'),
                'data-testid': 'guidelines-nav-link',
              }),
              ...insertIf(permissions?.view_paypal_commission, {
                key: ROUTES.AGENCY_MANAGEMENT.PAYPAL_COMMISSION,
                icon: <PaypalFilled />,
                label: t('nav.paypal_commission'),
                'data-testid': 'paypal-commission-nav-link',
              }),
            ],
          },
        ),
        ...insertIf(
          permissions?.view_pricing ||
            (featureFlags.financialDocuments && permissions?.view_finance) ||
            permissions?.view_users ||
            permissions?.view_partner_settings ||
            permissions?.view_api_docs ||
            permissions?.view_service_desk ||
            permissions?.manage_user_accounts,
          {
            type: 'divider',
          },
          {
            type: 'group',
            label: t('nav.group_administration').toUpperCase(),
            children: [
              ...insertIf(permissions?.view_pricing, {
                key: ROUTES.PRICING.INDEX,
                icon: <CoinsFilled />,
                label: t('nav.pricing'),
                'data-testid': 'pricing-nav-link',
              }),
              ...insertIf(featureFlags.financialDocuments && permissions?.view_finance, {
                key: ROUTES.FINANCES.FINANCIAL_DOCUMENTS,
                icon: <FileTextFilled />,
                label: t('nav.financial_documents'),
                'data-testid': 'financial-docs',
              }),
              ...insertIf(permissions?.view_users, {
                key: ROUTES.USERS.INDEX,
                icon: <UsersFilled />,
                label: t('nav.users'),
                'data-testid': 'user-list-nav-link',
              }),
              ...insertIf(permissions?.manage_user_accounts, {
                key: ROUTES.BOOKING_MANAGEMENT.CUSTOMERS,
                icon: <HomeFilled />,
                label: t('nav.b2c_customers'),
                'data-testid': 'customers-nav-link',
              }),
              ...insertIf(permissions?.view_partner_settings, {
                key: ROUTES.CONFIGURATION.SETTINGS,
                icon: <SettingFilled />,
                label: t('nav.settings'),
                'data-testid': 'settings-nav-link',
              }),
              ...insertIf(permissions?.view_api_docs, {
                key: ROUTES.API_DOCS,
                icon: <CodeFilled />,
                label: t('nav.api_docs'),
                'data-testid': 'api-docs-nav-link',
              }),
              ...insertIf(permissions?.view_service_desk, {
                key: ROUTES.SERVICE_DESK,
                icon: <CustomerServiceFilled />,
                label: t('nav.service_desk'),
                'data-testid': 'service-desk-nav-link',
              }),
            ],
          },
        ),
        ...insertIf(
          permissions?.view_whitelabel_customization_page || permissions?.edit_whitelabel_settings,
          {
            type: 'divider',
          },
          {
            type: 'group',
            label: t('nav.group_whitelabel').toUpperCase(),
            children: [
              {
                key: ROUTES.CONFIGURATION.WEBSITE_CONFIGURATION,
                icon: <CustomizationFilled />,
                label: t('nav.customization'),
                title: t('nav_titles.customization'),
                'data-testid': 'website-configuration-nav-link',
              },
            ],
          },
        ),
        ...insertIf(
          permissions?.view_prototypes,
          {
            type: 'divider',
          },
          {
            type: 'group',
            label: t('nav.inventory').toUpperCase(),
            children: [
              {
                key: ROUTES.CONFIGURATION.UPCOMING_FEATURES_PREVIEW,
                icon: <AppstoreAddOutlined />,
                label: t('nav.upcoming_featuires'),
                'data-testid': 'edit-upcoming-features-nav-link',
              },
            ],
          },
        ),
      ] as ItemTypeWithTestId[],
    [t, permissions],
  );

  const selectedItems = useMemo(
    () => getSelectedItems(navMenuItems, location),
    [location, navMenuItems],
  );

  const selectedKeys: string[] = useMemo(
    () => selectedItems.map((item) => item.key) as string[],
    [selectedItems],
  );

  const onClick: MenuProps['onClick'] = useCallback(
    ({ key }) => {
      if (key === ROUTES.BOOKING_MANAGEMENT.NEW) {
        history.push({ pathname: key, state: nanoid() });
      }
      if (selectedKeys.includes(key) && !matchPath(location.pathname, { path: key, exact: true })) {
        history.push(key);
      }

      notification.destroy();
      setMobileNavOpened(false);
    },
    [history, location, selectedKeys],
  );

  useEffect(() => {
    if (selectedItems.length) {
      const baseTitle = preferences?.tab_title || DEFAULT_TAB_TITLE;
      const documentTitle = [
        baseTitle,
        ...selectedItems.map(({ label, title }) => title || label),
      ].join(' > ');
      setDocumentTitle(documentTitle);
    }
  }, [preferences?.tab_title, selectedItems]);

  useEffect(() => {
    const unregister = history.listen(() => {
      performance.mark(PerformanceMarksEnum.Navigation);
    });
    return unregister;
  }, [history]);

  usePageActivationAsNavigationMark();

  useFramePaint({
    enabled: !isLoading,
    mark: PerformanceMarksEnum.LayoutTTI,
  });

  if (!isFetched && isLoading) {
    return <FullScreenSpin />;
  }

  return (
    <LayoutBase style={{ minHeight: '100%' }}>
      <SideNav
        mobileNavOpened={mobileNavOpened}
        onCloseMobileNav={() => setMobileNavOpened(false)}
        menuProps={{
          items: navMenuItems,
          selectedKeys,
          onSelect: handleSelect,
          onClick,
        }}
        preferences={preferences}
      />
      <LayoutBase>
        <Header>
          {!lg && (
            <Button
              type="text"
              size="large"
              icon={<MenuOutlined />}
              onClick={() => setMobileNavOpened(true)}
            />
          )}
        </Header>
        {children}
      </LayoutBase>
    </LayoutBase>
  );
};
